import { ADD_ON_NAMES } from '@chiroup/core/constants/chiroup-products';
import { STRIPE_PLANS } from '@chiroup/core/constants/prices';
import { createDecimal } from '@chiroup/core/functions/createDecimal';
import { formatCurrency } from '@chiroup/core/functions/format';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { Subscription, SubscriptionItem } from '@chiroup/core/types/Me.type';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import clinicService from '../../../../services/clinic.service';

type Props = {
  clinic: Partial<ListClinic>;
};

type StripeEnvironmentValues = Record<string, string | undefined>;

const matchesStripeValue = (values: StripeEnvironmentValues, value?: string) =>
  !!value && Object.values(values).includes(value);

const getProductId = (item: SubscriptionItem) =>
  typeof item.price.product === 'string' ? item.price.product : item.price.product?.id;

const getProductName = (item: SubscriptionItem) => {
  const { price } = item;
  const productId = getProductId(item);

  if (typeof price.product === 'object' && price.product.name) {
    return price.product.name;
  }

  const productNames: Array<[StripeEnvironmentValues, string]> = [
    [STRIPE_PLANS.STRIPE_PRODUCT_EHR_PLAN, 'EHR'],
    [STRIPE_PLANS.STRIPE_PRODUCT_BASIC_PLAN, 'Basic'],
    [STRIPE_PLANS.STRIPE_PRODUCT_PLUS_PLAN, 'Plus'],
    [STRIPE_PLANS.STRIPE_PRODUCT_PREMIUM_PLAN, 'Premium'],
    [STRIPE_PLANS.STRIPE_PRODUCT_LOCATIONS_PLAN, 'Additional locations'],
    [STRIPE_PLANS.STRIPE_PRODUCT_ADDITIONAL_PROVIDERS_PLAN, 'Additional providers'],
    [STRIPE_PLANS.STRIPE_PRODUCT_SOCIAL_MEDIA_ADDON_PLAN, 'Social media'],
    [STRIPE_PLANS.STRIPE_PRODUCT_NEWSLETTERS_PLAN, 'Newsletters'],
    [STRIPE_PLANS.STRIPE_PRODUCT_X12_LICENSURE_FEE_PLAN, 'X12 licensure fee'],
    [STRIPE_PLANS.STRIPE_PRODUCT_CLEARING_HOUSE_INTEGRATIONS_PLAN, 'Clearinghouse integrations'],
    [STRIPE_PLANS.STRIPE_PRODUCT_VOICE_TO_CHART_PLAN, ADD_ON_NAMES['voice-to-chart']],
    [STRIPE_PLANS.STRIPE_PRODUCT_ADVANCED_SMS_PLAN, ADD_ON_NAMES['two-way-texting']],
  ];
  const productName = productNames.find(([values]) => matchesStripeValue(values, productId))?.[1];

  if (productName) return productName;

  const priceNames: Array<[StripeEnvironmentValues, string]> = [
    [STRIPE_PLANS.STRIPE_EHR_PLAN, 'EHR'],
    [STRIPE_PLANS.STRIPE_BASIC_PLAN, 'Basic'],
    [STRIPE_PLANS.STRIPE_BASIC_PLAN_ANNUAL, 'Basic'],
    [STRIPE_PLANS.STRIPE_CANADA_BASIC_PLAN, 'Basic'],
    [STRIPE_PLANS.STRIPE_CANADA_BASIC_PLAN_ANNUAL, 'Basic'],
    [STRIPE_PLANS.STRIPE_ZAF_BASIC_PLAN, 'Basic'],
    [STRIPE_PLANS.STRIPE_PLUS_PLAN, 'Plus'],
    [STRIPE_PLANS.STRIPE_PLUS_PLAN_ANNUAL, 'Plus'],
    [STRIPE_PLANS.STRIPE_CANADA_PLUS_PLAN, 'Plus'],
    [STRIPE_PLANS.STRIPE_CANADA_PLUS_PLAN_ANNUAL, 'Plus'],
    [STRIPE_PLANS.STRIPE_ZAF_PLUS_PLAN, 'Plus'],
    [STRIPE_PLANS.STRIPE_PREMIUM_PLAN, 'Premium'],
    [STRIPE_PLANS.STRIPE_PREMIUM_PLAN_ANNUAL, 'Premium'],
    [STRIPE_PLANS.STRIPE_CANADA_PREMIUM_PLAN, 'Premium'],
    [STRIPE_PLANS.STRIPE_CANADA_PREMIUM_PLAN_ANNUAL, 'Premium'],
    [STRIPE_PLANS.STRIPE_ZAF_PREMIUM_PLAN, 'Premium'],
    [STRIPE_PLANS.STRIPE_ADDITIONAL_LOCATION, 'Additional locations'],
    [STRIPE_PLANS.STRIPE_ADDITIONAL_LOCATION_ANNUAL, 'Additional locations'],
    [STRIPE_PLANS.STRIPE_ZAF_ADDITIONAL_LOCATION, 'Additional locations'],
    [STRIPE_PLANS.STRIPE_VOICE_TO_CHART, ADD_ON_NAMES['voice-to-chart']],
    [STRIPE_PLANS.STRIPE_VOICE_TO_CHART_OVERAGE, `${ADD_ON_NAMES['voice-to-chart']} usage`],
    [STRIPE_PLANS.STRIPE_AI_RECEPTIONIST, ADD_ON_NAMES['ai-receptionist']],
    [STRIPE_PLANS.STRIPE_AI_RECEPTIONIST_LEGACY, ADD_ON_NAMES['ai-receptionist']],
    [STRIPE_PLANS.STRIPE_AI_RECEPTIONIST_OVERAGE, `${ADD_ON_NAMES['ai-receptionist']} usage`],
    [
      STRIPE_PLANS.STRIPE_AI_RECEPTIONIST_OVERAGE_LEGACY,
      `${ADD_ON_NAMES['ai-receptionist']} usage`,
    ],
    [STRIPE_PLANS.STRIPE_ADVANCED_MESSAGING, ADD_ON_NAMES['two-way-texting']],
    [STRIPE_PLANS.STRIPE_CANADA_ADVANCED_MESSAGING, ADD_ON_NAMES['two-way-texting']],
    [STRIPE_PLANS.STRIPE_ADVANCED_MESSAGING_OVERAGE, `${ADD_ON_NAMES['two-way-texting']} usage`],
    [
      STRIPE_PLANS.STRIPE_CANADA_ADVANCED_MESSAGING_OVERAGE,
      `${ADD_ON_NAMES['two-way-texting']} usage`,
    ],
  ];

  return (
    priceNames.find(([values]) => matchesStripeValue(values, price.id))?.[1] ||
    price.nickname ||
    `Stripe price ${price.id}`
  );
};

const formatStatus = (status?: string) => {
  if (!status) return 'Not connected';

  return status
    .split('_')
    .map(word => word.slice(0, 1).toUpperCase() + word.slice(1))
    .join(' ');
};

const getStatusClasses = (status?: string) => {
  if (status === 'active' || status === 'trialing') {
    return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200';
  }

  if (status === 'past_due' || status === 'requires_action' || status === 'incomplete') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200';
  }

  return 'bg-gray-100 text-gray-700 dark:bg-dark-gray-800 dark:text-dark-gray-200';
};

const formatPlan = (plan?: string) => {
  if (!plan) return 'Unknown';

  const withoutAnnual = plan.replace(/Annual$/, '');
  const name = withoutAnnual.slice(0, 1).toUpperCase() + withoutAnnual.slice(1);

  return plan.endsWith('Annual') ? `${name} (annual)` : name;
};

const formatRenewal = (subscription?: Subscription) => {
  if (!subscription) return 'Unavailable';

  if (
    subscription.status === 'canceled' ||
    subscription.status === 'expired' ||
    subscription.status === 'terminated'
  ) {
    return 'Ended';
  }

  return subscription.cancel_at_period_end ? 'Cancels at period end' : 'Renews automatically';
};

const formatItemPrice = (item: SubscriptionItem) => {
  const { price } = item;

  if (price.unit_amount === undefined || price.unit_amount === null) {
    return 'Price unavailable';
  }

  const currency = price.currency?.toUpperCase() || 'USD';
  const amount = createDecimal(price.unit_amount).dividedBy(100).toNumber();
  const formatted = formatCurrency(amount, currency);

  if (price.recurring?.usage_type === 'metered') {
    return `${formatted} ${currency} per unit`;
  }

  if (!price.recurring?.interval) return `${formatted} ${currency}`;

  const intervalCount = price.recurring.interval_count || 1;
  const interval = price.recurring.interval;

  return intervalCount === 1
    ? `${formatted} ${currency}/${interval}`
    : `${formatted} ${currency} every ${intervalCount} ${interval}s`;
};

type SubscriptionCardProps = {
  description: string;
  emptyText: string;
  fallbackPlan?: string;
  subscription?: Subscription;
  title: string;
};

const SubscriptionCard = ({
  description,
  emptyText,
  fallbackPlan,
  subscription,
  title,
}: SubscriptionCardProps) => {
  const items = subscription?.items || [];
  const periodEnd = subscription?.current_period_end
    ? dayjs.unix(subscription.current_period_end).format('MM/DD/YYYY')
    : undefined;
  const totalLocations = (subscription?.locations || 0) + 1;

  return (
    <article className="overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-dark-gray-800 dark:bg-dark-gray-900">
      <div className="border-b border-gray-200 px-4 py-5 dark:border-dark-gray-800 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-dark-gray-50">{title}</h2>
            <p className="text-sm text-gray-500 dark:text-dark-gray-400">{description}</p>
          </div>
          <div
            className={`rounded-full px-2.5 py-1 text-sm font-medium ${getStatusClasses(subscription?.status)}`}
          >
            {formatStatus(subscription?.status)}
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-6 px-4 py-5 sm:grid-cols-4 sm:px-6">
        <div>
          <dt className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">Plan</dt>
          <dd className="text-sm text-gray-500 dark:text-dark-gray-400">
            {formatPlan(subscription?.plan || fallbackPlan)}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">
            Billing period ends
          </dt>
          <dd className="text-sm text-gray-500 dark:text-dark-gray-400">
            {periodEnd || 'Unavailable'}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">Locations</dt>
          <dd className="text-sm text-gray-500 tabular-nums dark:text-dark-gray-400">
            {subscription ? totalLocations : 'Unavailable'}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">Renewal</dt>
          <dd className="text-sm text-gray-500 dark:text-dark-gray-400">
            {formatRenewal(subscription)}
          </dd>
        </div>
      </dl>

      <div className="border-t border-gray-200 dark:border-dark-gray-800">
        <h3 className="px-4 pt-5 text-sm font-medium text-gray-900 dark:text-dark-gray-100 sm:px-6">
          Products and add-ons
        </h3>
        {items.length ? (
          <ul role="list" className="divide-y divide-gray-200 dark:divide-dark-gray-800">
            {items.map(item => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-gray-100">
                    {getProductName(item)}
                  </p>
                  <p className="text-sm text-gray-500 tabular-nums dark:text-dark-gray-400">
                    {formatItemPrice(item)}
                  </p>
                </div>
                {item.price.recurring?.usage_type !== 'metered' && (
                  <p className="text-sm text-gray-500 tabular-nums dark:text-dark-gray-400">
                    Quantity {item.quantity || 1}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-5 text-sm text-gray-500 dark:text-dark-gray-400 sm:px-6">
            {emptyText}
          </p>
        )}
      </div>
    </article>
  );
};

const SubscriptionSummary = ({ clinic }: Props) => {
  const {
    data: customerSubscriptions = [],
    isError,
    isLoading,
  } = useQuery<Subscription[]>(
    ['clinic-subscriptions', clinic.ID],
    () => clinicService.listSubscriptions(clinic.ID as number),
    {
      enabled: !!clinic.ID && !!clinic.stripeCustomerId,
      retry: false,
    }
  );
  const listedCurrentSubscription = customerSubscriptions.find(
    subscription => subscription.id === clinic.stripeSubscriptionId
  );
  const currentSubscription = listedCurrentSubscription || clinic.subscription;
  const otherSubscriptions = customerSubscriptions.filter(
    subscription => subscription.id !== clinic.stripeSubscriptionId
  );

  return (
    <section
      className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pb-6 sm:px-6 lg:px-8"
      aria-label="Stripe subscriptions"
    >
      <SubscriptionCard
        title="Current subscription"
        description="Stripe plan, billing status, and subscribed products."
        subscription={currentSubscription}
        fallbackPlan={clinic.plan}
        emptyText={
          clinic.stripeSubscriptionId
            ? 'Stripe subscription details are currently unavailable.'
            : 'No Stripe subscription is connected to this clinic.'
        }
      />

      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-dark-gray-50">
            Other subscriptions
          </h2>
          <p className="text-sm text-gray-500 dark:text-dark-gray-400">
            Every other Stripe subscription attached to customer{' '}
            {clinic.stripeCustomerId || 'ID unavailable'}.
          </p>
        </div>

        {!clinic.stripeCustomerId ? (
          <p className="rounded-lg border border-gray-300 bg-white px-4 py-5 text-sm text-gray-500 dark:border-dark-gray-800 dark:bg-dark-gray-900 dark:text-dark-gray-400">
            No Stripe customer is connected to this clinic.
          </p>
        ) : isLoading ? (
          <p className="rounded-lg border border-gray-300 bg-white px-4 py-5 text-sm text-gray-500 dark:border-dark-gray-800 dark:bg-dark-gray-900 dark:text-dark-gray-400">
            Loading other subscriptions.
          </p>
        ) : isError ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            Other subscriptions could not be loaded from Stripe.
          </p>
        ) : otherSubscriptions.length ? (
          otherSubscriptions.map(subscription => (
            <SubscriptionCard
              key={subscription.id}
              title={`Subscription ${subscription.id}`}
              description="Another subscription attached to this Stripe customer."
              subscription={subscription}
              emptyText="This subscription has no Stripe line items."
            />
          ))
        ) : (
          <p className="rounded-lg border border-gray-300 bg-white px-4 py-5 text-sm text-gray-500 dark:border-dark-gray-800 dark:bg-dark-gray-900 dark:text-dark-gray-400">
            No other subscriptions were found for this Stripe customer.
          </p>
        )}
      </div>
    </section>
  );
};

export default SubscriptionSummary;
